// ============================================================================
// lib/admin/admin-switch-rules.ts — entre quels comptes on peut BASCULER (PUR).
// ============================================================================
// Le groupe = les comptes ADMIN + les comptes à adresse EXACTE `@jeraisonne.fr`
// (staff, comptes de démo, compte de revue store). Sous-domaines exclus :
// `@enfants.jeraisonne.fr` = vrais élèves gérés par leurs parents.
//   - DÉPART : n'importe quel compte du groupe, SAUF le compte de revue store
//     (`app_tester@jeraisonne.fr`, code de connexion fixe connu d'Apple/Google :
//     il ne doit jamais ouvrir le compte admin) ;
//   - ARRIVÉE : n'importe quel compte du groupe (revue store comprise), jamais soi-même.
// Miroir serveur : `supabase/functions/_shared/admin-switch.ts` (parité testée
// par `_shared/admin-switch.test.ts`).
// ============================================================================

/** Comptes du groupe qui ne peuvent PAS lancer une bascule (connexion publique). */
export const SWITCH_SOURCE_BLOCKED_EMAILS = ['app_tester@jeraisonne.fr'];

export type AdminSwitchBlockReason = 'self' | 'not_internal';

function normEmail(email: string | null | undefined): string {
  return (email ?? '').trim().toLowerCase();
}

/** Vrai pour une adresse exacte `@jeraisonne.fr` (sous-domaines exclus). */
export function isInternalStaffEmail(email: string | null | undefined): boolean {
  const e = normEmail(email);
  const at = e.lastIndexOf('@');
  return at > 0 && e.slice(at + 1) === 'jeraisonne.fr';
}

/** Compte du groupe : admin ou adresse `@jeraisonne.fr`. */
export function isSwitchableAccount(input: {
  email: string | null | undefined;
  role: string | null | undefined;
}): boolean {
  return input.role === 'admin' || isInternalStaffEmail(input.email);
}

/** Ce compte peut-il OUVRIR le sélecteur et basculer ? */
export function canStartAccountSwitch(input: {
  email: string | null | undefined;
  role: string | null | undefined;
}): boolean {
  if (SWITCH_SOURCE_BLOCKED_EMAILS.includes(normEmail(input.email))) return false;
  return isSwitchableAccount(input);
}

/** Raison qui empêche d'arriver sur ce compte, ou `null` si c'est permis. */
export function adminSwitchBlockReason(input: {
  email: string | null | undefined;
  role?: string | null;
  targetId: string;
  actorId: string;
}): AdminSwitchBlockReason | null {
  if (input.targetId === input.actorId) return 'self';
  if (!isSwitchableAccount({ email: input.email, role: input.role ?? null })) {
    return 'not_internal';
  }
  return null;
}
